/**
 * UUID utilities for generating and parsing endpoint UUIDs in legacy format
 * Legacy UUID format: ${kind}-${nodeId}_${randomNumber}
 * Special case: start_node uses "start_node-source"
 */
import type { LegacyNodeKind } from './legacyNodes'

/**
 * Generate a cryptographically secure random number.
 * Uses crypto.getRandomValues to generate a 13-digit number in the range [1000000000000, 9999999999999].
 * This matches the format used by legacy for UUID generation.
 *
 * @returns A 13-digit random number in the range [1000000000000, 9999999999999]
 * @throws Error if crypto.getRandomValues is not available (should not happen in modern browsers)
 */
export function generateSecureRandomNumber(): number {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    throw new Error(
      'crypto.getRandomValues is not available. This should not happen in modern browsers.',
    )
  }
  const array = new Uint32Array(2)
  crypto.getRandomValues(array)
  const combined = BigInt(array[0]) * BigInt(0x100000000) + BigInt(array[1])
  const min = BigInt(1000000000000)
  const range = BigInt(9000000000000)
  const result = min + (combined % range)
  return Number(result)
}

/**
 * Extract numeric ID from node ID.
 * Node IDs may be in format "sound_clip-1767644025462" (with kind prefix) or just "1767644025462" (numeric only).
 * Legacy expects just the numeric part for UUID generation.
 *
 * Note: This edge case (non-numeric nodeIds) cannot occur in normal operation:
 * - Admin3 generates: `id: ${pendingNodeType}-${Date.now()}` → numeric timestamps
 * - Legacy uses numeric DOM element IDs: `$elem.attr('id')` is always numeric
 * - UUID generation uses these numeric IDs: `${kind}-${numericId}_${randomNumber}`
 * However, we validate defensively to provide a safety net for unexpected edge cases.
 *
 * @param nodeId - The node ID (may include kind prefix like "sound_clip-1767644025462" or be numeric only)
 * @returns The numeric ID part (e.g., "1767644025462"). If nodeId contains '-', returns the part after the last '-'.
 */
export function extractNumericId(nodeId: string): string {
  let extracted: string
  if (nodeId.includes('-')) {
    extracted = nodeId.split('-').pop() || nodeId
  } else {
    extracted = nodeId
  }

  // Defensive validation: ensure extracted ID is numeric
  // This edge case cannot occur in normal operation but provides safety net
  if (!/^\d+$/.test(extracted)) {
    console.warn(
      `[UUID Utils] Non-numeric node ID extracted: "${extracted}" from "${nodeId}". This should not occur in normal operation as both Admin3 and Legacy generate numeric IDs.`,
    )
  }

  return extracted
}

/**
 * Generate a unique random number, ensuring no collisions with previously generated numbers.
 * Uses a Set to track used random numbers and regenerates if a collision occurs (very unlikely).
 * The generated number is automatically added to the usedRandomNumbers set.
 *
 * @param usedRandomNumbers - Set of already used random numbers to avoid collisions
 * @returns A unique random number that is not in the usedRandomNumbers set
 */
export function getUniqueRandomNumber(usedRandomNumbers: Set<number>): number {
  let random: number
  do {
    random = generateSecureRandomNumber()
  } while (usedRandomNumbers.has(random))
  usedRandomNumbers.add(random)
  return random
}

/**
 * Generate an endpoint UUID in legacy format.
 * Format: ${kind}-${nodeId}_${randomNumber}
 * Special case: start_node uses "start_node-source"
 *
 * Note: The UUID format does NOT include the handle name. Legacy identifies handles by anchor
 * and my_label, not by UUID format. Each handle gets its own UUID with a unique random number.
 *
 * Start Node Special Case:
 * The start_node uses a special UUID format: "start_node-source" (not the standard format).
 * This is a legacy requirement because:
 * 1. Start node is the entry point and has a fixed source endpoint
 * 2. Legacy code expects this exact UUID format for the start node source
 * 3. This UUID should NEVER be used as a target - it's only for the start node's source endpoint
 * 4. The start node has no targets array (uses [{}] pattern), so this UUID is never referenced as target_uuid
 *
 * @param kind - The legacy node kind (e.g., 'simple_dial', 'queue', 'auto_attendant')
 * @param nodeId - The node ID (will be converted to numeric ID via extractNumericId)
 * @param randomNumber - The random number to use (must be unique for each endpoint)
 * @param _handleName - Unused parameter (kept for backwards compatibility)
 * @returns The endpoint UUID in legacy format (e.g., "simple_dial-1234567890123_9876543210987" or "start_node-source")
 */
export function generateEndpointUuid(
  kind: LegacyNodeKind,
  nodeId: string,
  randomNumber: number,
  _handleName?: string,
): string {
  // Special case: start_node uses fixed UUID "start_node-source"
  // This UUID should NEVER be used as a target - it's only for the start node's source endpoint
  if (kind === 'start_node') {
    return 'start_node-source'
  }

  const numericId = extractNumericId(nodeId)
  return `${kind}-${numericId}_${randomNumber}`
}

/**
 * Parse an endpoint UUID to extract its components (kind, nodeId, and randomNumber).
 * Handles multiple UUID formats:
 * - Standard format: ${kind}-${nodeId}_${randomNumber}
 * - Target endpoint format: ${kind}-${nodeId}-target_${randomNumber}
 * - Special case: "start_node-source"
 *
 * Legacy extracts node ID using: split('-')[1].split('_')[0]
 *
 * @param uuid - The endpoint UUID in legacy format
 * @returns An object with kind, nodeId, and randomNumber properties, or null if parsing fails.
 *   For "start_node-source", returns { kind: 'start_node', nodeId: 'start_node', randomNumber: '' }
 */
export function parseEndpointUuid(uuid: string): {
  kind: string
  nodeId: string
  randomNumber: string
} | null {
  if (uuid === 'start_node-source') {
    return {
      kind: 'start_node',
      nodeId: 'start_node',
      randomNumber: '',
    }
  }

  const hasTargetSuffix = uuid.includes('-target_')

  if (hasTargetSuffix) {
    const targetIndex = uuid.indexOf('-target_')
    const beforeTarget = uuid.substring(0, targetIndex)
    const afterTarget = uuid.substring(targetIndex + '-target_'.length)
    const parts = beforeTarget.split('-')
    if (parts.length < 2) {
      return null
    }
    const kind = parts[0]
    const nodeId = parts.slice(1).join('-')
    const randomNumber = afterTarget
    if (!nodeId || !randomNumber) {
      return null
    }
    return {
      kind,
      nodeId,
      randomNumber,
    }
  }

  const parts = uuid.split('-')
  if (parts.length < 2) {
    return null
  }

  const kind = parts[0]
  const rest = parts.slice(1).join('-')
  const [nodeId, randomNum] = rest.split('_')

  if (!nodeId || !randomNum) {
    return null
  }

  return {
    kind,
    nodeId,
    randomNumber: randomNum,
  }
}

/**
 * Extract node ID from endpoint UUID for matching with node.id.
 * Legacy uses this to match target_uuid to node.id when building connections.
 * This is a convenience function that parses the UUID and returns just the nodeId part.
 *
 * @param uuid - The endpoint UUID in legacy format
 * @returns The node ID that should match a node's id field (numeric ID for most nodes, "start_node" for start nodes),
 *   or null if parsing fails
 */
export function extractNodeIdFromUuid(uuid: string): string | null {
  const parsed = parseEndpointUuid(uuid)
  if (!parsed) {
    return null
  }

  if (parsed.kind === 'start_node') {
    return 'start_node'
  }

  return parsed.nodeId
}
